// FILE: assets/bold-quantity-picker.js
if (!customElements.get("bold-quantity-picker")) {
    customElements.define(
        "bold-quantity-picker",
        class BoldQuantityPicker extends HTMLElement {
            constructor() {
                super();
                this.numericInput = null;
                this.rangeInput = null;
                this.wrapper = null;
                this.facade = null;
                this.isInitialized = false;
                this.isUnlocked = false;
                this.originalMatrixInput = null;

                this.SLIDER_ZONES = [
                    { max: 600, step: 1 },    
                    { max: 1200, step: 5 }
                ];

                this.calculatedZones = [];
                this.sliderMax = 0;
                
                this._handleSliderChange = this._handleSliderChange.bind(this);
                this._handleMatrixInput = this._handleMatrixInput.bind(this);
                this._handleNumericInput = this._handleNumericInput.bind(this);
                this._checkUnlock = this._checkUnlock.bind(this);
                //this._moveTable = this._moveTable.bind(this);

                this.EVENTS = {
                    STATE_CHANGE_GENERAL: 'hub:state:change', 
                };
            }

            connectedCallback() {
                if (this.isInitialized) {
                    if (!this.isUnlocked) {
                        document.addEventListener("change", this._checkUnlock);
                        document.addEventListener("input", this._checkUnlock);
                        document.addEventListener(
                            this.EVENTS.STATE_CHANGE_GENERAL,
                            this._checkUnlock
                        );

                        this._checkUnlock();
                    }

                    return;
                }

                const adapter = document.querySelector("bold-sc-adapter");
                this.facade = adapter ? adapter.facade : null;

                if (this.facade && adapter.getAttribute("options") === "ready") {
                    this.init();
                } else {
                    document.addEventListener("hub:options:ready", () => {
                        const tryInit = (attempt = 0) => {
                            const a = document.querySelector("bold-sc-adapter");
                            this.facade = a ? a.facade : null;
                            if (this.facade || attempt >= 5) {
                                this.init();
                            } else {
                                setTimeout(() => tryInit(attempt + 1), 100);
                            }
                        };
                        tryInit();
                    }, { once: true });
                }

                //document.addEventListener(this.EVENTS.QUANTITY_BREAKS_LOADED, this._moveTable);
            }

            disconnectedCallback() {
                // Clean up listeners
                document.removeEventListener("change", this._checkUnlock);
                document.removeEventListener("input", this._checkUnlock);
                document.removeEventListener(
                    this.EVENTS.STATE_CHANGE_GENERAL,
                    this._checkUnlock
                );
            }

            init() {
                if (this.isInitialized) return;
                this.isInitialized = true;
                this.classList.remove("bold-initial-hide");
                this.numericInput = document.querySelector('input[name="quantity"], .quantity__input');
                if (!this.numericInput) return;
                this.wrapper =
                    this.numericInput.closest(".product-form__quantity, .quantity") || this.numericInput.parentElement;

                // TODO: Move initialization to price display
                const receipt = document.querySelector("bold-price-display");
                if (receipt) {
                    receipt.classList.remove("bold-mode-matrix");
                    receipt.classList.remove("bold-mode-integrated");
                }
                const matrixTarget = this._getMatrixTarget();
                if (matrixTarget) {
                    const safeName = CSS.escape(`properties[${matrixTarget.prettyName}]`);
                    this.originalMatrixInput = document.querySelector(`[name="${safeName}"]`);
                    this._swapPosition(matrixTarget.name);
                    this._renderMatrixMode(matrixTarget);
                    //this._moveTable();
                } else {
                    this._renderSliderMode();
                    //this._moveTable();
                }

                this._initProgressiveDisclosure();

                setTimeout(() => {
                    if (!matrixTarget) this._mergeReceipt();
                    this.facade.tools.dispatch('QUANTITY_PICKER_LOADED', { isMatrix: !!matrixTarget });
                    this._checkUnlock();
                }, 100);
            }

            // 5. NEW: Separate method to update quantity display only (fired by QTY_TOTAL_UPDATED)
            _updateQtyDisplay() {
                if (!this.facade) return;
                const total = this.facade.state.getQuantity();

                // Update hidden numeric input
                if (this.numericInput) {
                    this.numericInput.value = total;
                    this.numericInput.dispatchEvent(new Event("input", { bubbles: true }));
                    this.numericInput.dispatchEvent(new Event("change", { bubbles: true }));
                }
            }

            // 1) Add these helpers anywhere inside the class (e.g. above _initProgressiveDisclosure)

            _getColorExcludePhrases() {
            // Optional: allow overrides from markup:
            // <bold-quantity-picker data-color-exclude="# of colors, number of colors, ink colors"></bold-quantity-picker>
            const attr = (this.getAttribute('data-color-exclude') || '')
                .split(',')
                .map(s => s.trim().toLowerCase())
                .filter(Boolean);

            // Defaults (add more here as needed)
            const defaults = [
                '# of colors for front design',
                '# of colors for back design',
                '# of colors for left sleeve design',
                '# of colors for right sleeve design',
                'ink colors (front)',
                'ink colors (back)',
                'ink colors (left sleeve)',
                'ink colors (right sleeve)'
            ];

            // Dedup
            return Array.from(new Set([...defaults, ...attr]));
            }

            _isExcludedColorText(text) {
            const t = String(text || '').toLowerCase();
            const phrases = this._getColorExcludePhrases();
            return phrases.some(p => p && t.includes(p));
            }

            _isRealColorOption(opt) {
            const name = String(opt?.name || '');
            const pretty = String(opt?.prettyName || '');
            const haystack = `${name} ${pretty}`.trim();

            // Must actually be a "color/colour" option…
            const looksLikeColor = /\bcolou?rs?\b/i.test(haystack);
            if (!looksLikeColor) return false;

            // …but NOT one of the excluded “color count / ink color” style options
            if (this._isExcludedColorText(haystack)) return false;

            return true;
            }

            _isRealColorKey(key) {
            // selections keys are labels like "Shirt Color", "Front # of Colors", etc.
            const k = String(key || '');
            if (!/\bcolou?rs?\b/i.test(k)) return false;
            if (this._isExcludedColorText(k)) return false;
            return true;
            }

            _initProgressiveDisclosure() {
                if (!this.facade || !this.facade.options) {
                    this.isUnlocked = true;
                    this._togglePlaceholder(false);
                    return;
                }

                const options = this.facade.options.getOptionSets();
                this.colorOption = options.find((o) => this._isRealColorOption(o));

                if (!this.colorOption) {
                    this.isUnlocked = true;
                    this._togglePlaceholder(false);
                    return;
                }

                this.isUnlocked = false;
                this._togglePlaceholder(true);

                document.addEventListener("change", this._checkUnlock);
                document.addEventListener("input", this._checkUnlock);
                document.addEventListener(
                    this.EVENTS.STATE_CHANGE_GENERAL,
                    this._checkUnlock
                );

                // Check immediately in case defaults are already applied.
                this._checkUnlock();

                // Check again after DOM/state finishes settling.
                requestAnimationFrame(() => {
                    this._checkUnlock();
                });

                setTimeout(() => {
                    this._checkUnlock();
                }, 100);
            }
            
            _checkUnlock() {
                if (!this.facade || !this.facade.state) return;
                if (!this.colorOption) return;

                const selections =
                    this.facade.state.getSelection?.() || {};

                const foundSelection = Object.entries(selections).find(
                    ([key, value]) => {
                        if (!this._isRealColorKey(key)) return false;

                        if (value === null || value === undefined) {
                            return false;
                        }

                        if (
                            typeof value === "string" &&
                            value.trim() === ""
                        ) {
                            return false;
                        }

                        return true;
                    }
                );

                let domHasSelection = false;

                const optionName =
                    this.colorOption.prettyName ||
                    this.colorOption.name;

                if (optionName) {
                    const safeName = CSS.escape(
                        `properties[${optionName}]`
                    );

                    const input = document.querySelector(
                        `[name="${safeName}"]`
                    );

                    if (input) {
                        if (input.tagName === "SELECT") {
                            domHasSelection =
                                !!input.value &&
                                !input.value.startsWith("--");
                        } else if (
                            input.type === "radio" ||
                            input.type === "checkbox"
                        ) {
                            const checked = document.querySelector(
                                `[name="${safeName}"]:checked`
                            );

                            domHasSelection = !!checked;
                        } else {
                            domHasSelection =
                                String(input.value || "").trim() !== "";
                        }
                    }
                }

                const shouldUnlock =
                    !!foundSelection || domHasSelection;

                if (!shouldUnlock) return;

                const wasUnlocked = this.isUnlocked;

                this.isUnlocked = true;

                // Always reconcile the UI even if state already thought it was unlocked.
                this._togglePlaceholder(false);

                if (
                    !wasUnlocked &&
                    this.facade.tools &&
                    typeof this.facade.tools.dispatch === "function"
                ) {
                    this.facade.tools.dispatch("UI_UNLOCK", {
                        field: foundSelection
                            ? foundSelection[0]
                            : optionName,
                        reason: "selection-confirmed",
                    });
                }
            }

            _togglePlaceholder(show) {
                const container = this.querySelector(".quantity-slider-group");
                if (!container) return;
                const inputs =
                    container.querySelector(".matrix-grid-container") || container.querySelector(".qty-controls-row");
                let existingPlaceholder = container.querySelector(".bold-qty-placeholder");
                if (show) {
                    if (inputs) inputs.style.display = "none";
                    if (!existingPlaceholder) {
                        existingPlaceholder = document.createElement("div");
                        existingPlaceholder.className = "bold-qty-placeholder bold-load-animation";
                        const label = this.colorOption ? this.colorOption.prettyName || "Color" : "Color";
                        existingPlaceholder.innerHTML = `<span>Select a <strong>${label}</strong> above to add sizes</span>`;
                        if (inputs) container.insertBefore(existingPlaceholder, inputs);
                        else container.prepend(existingPlaceholder);
                    }
                } else {
                    if (inputs) inputs.style.display = inputs.classList.contains("qty-controls-row") ? "flex" : "grid";
                    if (existingPlaceholder) existingPlaceholder.remove();
                }
            }

            _mergeReceipt() {
                const receipt = document.querySelector("bold-price-display");
                const mainContainer = this.closest(".bold-quantity-block");
                if (receipt && mainContainer) {
                    mainContainer.appendChild(receipt);
                    const baseRow = receipt.querySelector('.bold-row:has([data-slot="base"])');
                    if (baseRow) baseRow.style.setProperty("display", "none", "important");
                    const unitRow = receipt.querySelector('[data-row="unit-total"]');
                    if (unitRow) unitRow.style.setProperty("display", "none", "important");
                    const greyBox = mainContainer.querySelector(".quantity-slider-group");
                    if (greyBox) greyBox.classList.add("has-integrated-footer");
                }
            }

            _getMatrixTarget() {
                if (!this.facade) return null;

                const allOptions = this.facade.options.getOptionSets();

                const sizeCandidates = allOptions.filter((opt) => {
                    const name = String(opt.name || '')
                        .replace(/\*/g, '')
                        .trim()
                        .toLowerCase();

                    return /^(size|sizes)(?:\s*#?2)?$/.test(name);
                });

                const hasPrimarySize = sizeCandidates.some((opt) => {
                    const name = String(opt.name || '')
                        .replace(/\*/g, '')
                        .trim()
                        .toLowerCase();

                    return name === 'size' || name === 'sizes';
                });

                const hasSecondSize = sizeCandidates.some((opt) => {
                    const name = String(opt.name || '')
                        .replace(/\*/g, '')
                        .trim()
                        .toLowerCase();

                    return /^(size|sizes)\s*#?2$/.test(name);
                });

                // Package product: Size + Size #2
                // Do not use the single-product matrix grid.
                if (hasPrimarySize && hasSecondSize) {
                    return null;
                }

                const target = sizeCandidates.find((opt) => {
                    const name = String(opt.name || '')
                        .replace(/\*/g, '')
                        .trim()
                        .toLowerCase();

                    return name === 'size' || name === 'sizes';
                });

                if (!target) return null;

                if (target.values.length > 20) return null;

                return target;
            }

            _swapPosition(targetName) {
                const safeName = targetName.replace(/"/g, '\\"');
                let originalInput = document.querySelector(`[name="properties[${safeName}]"]`);
                if (!originalInput)
                    originalInput = document.querySelector(`[name="properties[Size]"], [name="properties[size]"]`);
                if (originalInput) {
                    const container = originalInput.closest(".bold_option");
                    if (container && container.parentNode) {
                        container.classList.add("bold_option_hidden");
                        container.style.display = "none";
                        // container.parentNode.insertBefore(this, container.nextSibling);
                        // Moves to end of first container to ensure it is the last option in Step 1
                        container.parentNode.append(this);
                    }
                    originalInput.removeAttribute("required");
                }
                const form = this.closest("form");
                /*
                const submitWrapper = form ? form.querySelector(".product-form__buttons") : null;
                if (submitWrapper) {
                    submitWrapper.style.display = "none";
                }
                */
            }

            _renderMatrixMode(optionData) {
                document.body.classList.add("matrix-mode-active");
                const receipt = document.querySelector("bold-price-display");
                if (receipt) receipt.classList.add("bold-mode-matrix");
                if (this.wrapper) this.wrapper.style.display = "none";
                const headerLabel = `${optionData.prettyName} & Quantity`;
                this.innerHTML = `<div class="bold_option bold_option_displaytext"><span class="bold_option_title">${headerLabel}</span><span class="bold_option_element"><p>${headerLabel}</p></span></div><div class="quantity-slider-group matrix-wrapper"><div class="matrix-grid-container" style="background: transparent; padding: 0; margin-bottom: 0;">${optionData.valuesData.map((val) => this._renderMatrixCard(val)).join("")}</div></div>`;
                this.optionName = optionData.prettyName;
                this.querySelectorAll(".matrix-qty-input").forEach((input) => {
                    input.addEventListener("input", this._handleMatrixInput);
                    input.addEventListener("change", this._handleMatrixInput);
                });
                optionData.valuesData.forEach((val) => {
                    this.facade.actions.updateMatrixQuantitySilent(this.optionName, val.value, 0);
                });
                this.facade.actions.recalculateAll();
                // this._checkUnlock();
                // this._moveTable();
            }

            _renderMatrixCard(val) {
                const priceTxt = val.price > 0 ? `+ $${(val.price / 100).toFixed(2)}` : "";
                const labelText = this.facade.helpers.normalizeSizeLabel ? this.facade.helpers.normalizeSizeLabel(val.value) : val.value;
                return `<div class="matrix-card"><div class="matrix-info"><span class="matrix-label">${labelText}</span><span class="matrix-price">${priceTxt}</span></div><div class="matrix-input-wrapper"><div class="matrix-quantity"><button class="matrix-stepper-btn" type="button" onclick="this.nextElementSibling.stepDown(); this.nextElementSibling.dispatchEvent(new Event('input', {bubbles:true}))"><svg width="10" height="2" viewBox="0 0 10 2" fill="currentColor"><path d="M0 0h10v2H0z"/></svg></button><input class="matrix-qty-input" type="number" min="0" data-value-name="${val.value}" value="0" placeholder="0" autocomplete="off"><button class="matrix-stepper-btn" type="button" onclick="this.previousElementSibling.stepUp(); this.previousElementSibling.dispatchEvent(new Event('input', {bubbles:true}))"><svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><path d="M4 4V0h2v4h4v2H6v4H4V6H0V4h4z"/></svg></button></div></div></div>`;
            }

            _handleMatrixInput(e) {
                const input = e.target;
                const valueName = input.dataset.valueName;
                const qty = parseInt(input.value, 10) || 0;
                this.facade.actions.updateMatrixQuantity(this.optionName, valueName, qty);
                const card = input.closest(".matrix-card");
                if (qty > 0) card.classList.add("is-active");
                else card.classList.remove("is-active");
                if (this.originalMatrixInput) {
                    const selectedSize =
                        Array.from(this.querySelectorAll(".matrix-card.is-active")).map(
                            (c) => c.querySelector(".matrix-qty-input").dataset.valueName
                        )[0] || "";
                    if (selectedSize) {
                        this.originalMatrixInput.value = selectedSize;
                        this.originalMatrixInput.dispatchEvent(new Event("change", { bubbles: true }));
                    }
                }
            }

            _renderSliderMode() {
                this._initSliderMath();
                const min = 1;
                const max = this.sliderMax;
                const currentReal = parseInt(this.numericInput.value) || 1;
                const currentSlider = this._getSliderValue(currentReal);
                const displayMax = this.SLIDER_ZONES[this.SLIDER_ZONES.length - 1].max;
                this.innerHTML = `<div class="qty-slider-container"><input type="range" class="qty-range-input" min="${min}" max="${max}" value="${currentSlider}" step="1"><div class="qty-slider-labels"><span class="qty-minmax">${min}</span><span class="qty-minmax">${displayMax}+</span></div></div>`;
                this.rangeInput = this.querySelector(".qty-range-input");
                this.rangeInput.addEventListener("input", this._handleSliderChange);
                this.numericInput.addEventListener("input", this._handleNumericInput);
                this.numericInput.addEventListener("change", this._handleNumericInput);
                if (this.wrapper && this.wrapper.parentNode) {
                    const parent = this.wrapper.parentNode;
                    const mainContainer = document.createElement("div");
                    mainContainer.className = "bold-quantity-block";
                    mainContainer.style.width = "100%";
                    mainContainer.style.marginBottom = "20px";
                    const sectionHeader = document.createElement("div");
                    sectionHeader.className = "bold_option bold_option_displaytext";
                    sectionHeader.innerHTML = `<span class="bold_option_title">Quantity</span><span class="bold_option_element"><p>Quantity</p></span>`;
                    const flexGroup = document.createElement("div");
                    flexGroup.className = "quantity-slider-group";
                    const controlsRow = document.createElement("div");
                    controlsRow.className = "qty-controls-row";
                    parent.insertBefore(mainContainer, this.wrapper);
                    mainContainer.appendChild(sectionHeader);
                    mainContainer.appendChild(flexGroup);
                    flexGroup.appendChild(controlsRow);
                    controlsRow.appendChild(this);
                    controlsRow.appendChild(this.wrapper);
                }
            }
            _initSliderMath() {
                let realCursor = 0;
                let sliderCursor = 0;
                this.calculatedZones = this.SLIDER_ZONES.map((zone) => {
                    const realRange = zone.max - realCursor;
                    const sliderTicks = Math.ceil(realRange / zone.step);
                    const data = {
                        realMin: realCursor,
                        realMax: zone.max,
                        step: zone.step,
                        sliderMin: sliderCursor,
                        sliderMax: sliderCursor + sliderTicks,
                    };
                    realCursor = zone.max;
                    sliderCursor += sliderTicks;
                    return data;
                });
                this.sliderMax = sliderCursor;
                console.log('facade state', this.facade.state);
            }
            _getRealValue(sliderVal) {
                const v = parseInt(sliderVal, 10);
                const zone =
                    this.calculatedZones.find((z) => v <= z.sliderMax) ||
                    this.calculatedZones[this.calculatedZones.length - 1];
                const val = zone.realMin + (v - zone.sliderMin) * zone.step;
                return Math.min(val, zone.realMax);
            }
            _getSliderValue(realQty) {
                const qty = parseInt(realQty, 10);
                const zone =
                    this.calculatedZones.find((z) => qty <= z.realMax) ||
                    this.calculatedZones[this.calculatedZones.length - 1];
                return zone.sliderMin + Math.round((qty - zone.realMin) / zone.step);
            }
            _handleSliderChange(e) {
                const realQty = this._getRealValue(e.target.value);
                // Update numeric display immediately so user sees value change
                this.numericInput.value = realQty;
                // Update numeric input display in the numericInput
                this._showSliderLoading(true);
                // Debounce the facade recalc — only fire when user stops dragging
                clearTimeout(this._sliderDebounce);
                this._sliderDebounce = setTimeout(() => {
                    this.numericInput.dispatchEvent(new Event("change", { bubbles: true }));
                    this._showSliderLoading(false);
                }, 300);
            }
            _showSliderLoading(show) {
                // Dim the two qty counter elements during debounce
                const targets = [
                    document.getElementById('header-qty-counter'),
                    document.querySelector('[data-slot="qty"]'),
                    document.querySelector('[data-slot="grand-total"]'),
                ];
                targets.forEach(el => {
                    if (el) el.style.opacity = show ? '0.35' : '';
                });
            }
            _handleNumericInput(e) {
                const sliderVal = this._getSliderValue(e.target.value);
                if (this.rangeInput) this.rangeInput.value = sliderVal;
            }
        }
    );
}